import { memo, useId, useLayoutEffect, useRef } from 'react';

import Flame from 'components/Streaks/Flame';
import Freeze from 'components/Streaks/Freeze';
import { derivePanelView, StreakView } from 'components/Streaks/model/deriveView';
import { StreakSnapshot } from 'components/Streaks/model/types';

import styles from './StreakPanel.module.scss';

const CHIP_BG = [
  '',
  '',
  'linear-gradient(180deg, #ffd089 0%, #e8a04a 100%)',
  'linear-gradient(180deg, #ffe58f 0%, #e9b43a 100%)',
  'linear-gradient(180deg, #ffffff 0%, #e9dcc0 100%)',
  'linear-gradient(180deg, #f1f7ff 0%, #c9dcf5 100%)',
  'linear-gradient(180deg, #d8eeff 0%, #8fb8ff 100%)'
];

/** Rail cells (EN-11162): 88 px wide, 8 px apart; flame centers 17 px into each cell. */
const RAIL_CELL = 88;
const RAIL_STEP = RAIL_CELL + 8;
/** The track's left edge: the rail's 10 px inner padding plus the flame's 17 px center. */
const RAIL_TRACK_LEFT = 10 + 17;

const NOTICE_BG = {
  info: 'rgba(232,130,31,0.16)',
  snow: 'rgba(47,111,181,0.14)',
  ended: 'rgba(43,23,10,0.07)'
};

export interface StreakPanelProps {
  view: StreakView;
  snapshot: StreakSnapshot;
  expanded: boolean;
  /** The card just opened from a tap (plays the 220 ms fade); not on first paint. */
  animateIn: boolean;
  reducedMotion: boolean;
  onToggle: () => void;
  onBuy: () => void;
  onHowItWorks: () => void;
}

/**
 * The account-panel streak block (EN-11162): one collapsed row under the
 * balance that expands in place into the card. The ladder renders every
 * milestone the snapshot carries, so a sixth rung needs no change here.
 */
function StreakPanel({
  view,
  snapshot,
  expanded,
  animateIn,
  reducedMotion,
  onToggle,
  onBuy,
  onHowItWorks
}: StreakPanelProps) {
  const p = derivePanelView(view, snapshot);
  const cardId = useId();
  const railRef = useRef<HTMLDivElement>(null);

  // The rail opens on the player's current status, so the next two are in view
  // (a day-100 Marshal sees Marshal · Baron · Legend); the browser clamps it to the last stop.
  useLayoutEffect(() => {
    if (expanded && railRef.current) railRef.current.scrollLeft = p.railStart * RAIL_STEP;
  }, [expanded, p.railStart]);
  const stage = view.flameStage;
  const noticeBg = p.notice
    ? view.state === 'missed'
      ? NOTICE_BG.ended
      : NOTICE_BG[p.notice.kind]
    : undefined;

  return (
    <div
      className={`${styles.block} ${expanded ? styles.open : ''} ${
        reducedMotion ? styles.still : ''
      }`}
      data-testid='streak-panel'>
      <button
        type='button'
        className={styles.row}
        aria-expanded={expanded}
        aria-controls={cardId}
        onClick={onToggle}>
        <Flame stage={stage} size={40} low={view.state === 'lastcall'} />
        <span className={styles.rowText}>
          <span className={styles.rowTitle}>
            {p.rowTitle}
            {p.chip && (
              <span
                className={styles.chip}
                style={{ background: CHIP_BG[p.chip.stage] || CHIP_BG[2] }}>
                {p.chip.name}
              </span>
            )}
          </span>
          <span className={`${styles.rowSub} ${p.rowSubAlert ? styles.alertSub : ''}`}>
            {p.rowSub}
          </span>
        </span>
        <span className={styles.chevronWrap} aria-hidden='true'>
          <svg
            className={styles.chevron}
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#ffb45c'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'>
            <path d='M6 9l6 6 6-6' />
          </svg>
        </span>
      </button>

      {expanded && (
        <div id={cardId} className={`${styles.card} ${animateIn ? styles.cardIn : ''}`}>
          <svg className={styles.grain} aria-hidden='true'>
            <filter id='spGrain'>
              <feTurbulence
                type='fractalNoise'
                baseFrequency='0.85'
                numOctaves={2}
                stitchTiles='stitch'
              />
              <feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.18 0' />
            </filter>
            <rect width='100%' height='100%' filter='url(#spGrain)' />
          </svg>
          <span className={styles.dashed} aria-hidden='true' />

          <div className={styles.cardBody}>
            {p.notice && (
              <div className={styles.notice} style={{ background: noticeBg }} role='status'>
                {p.notice.kind === 'snow' ? (
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#2f6fb5'
                    strokeWidth='2.4'
                    strokeLinecap='round'
                    aria-hidden='true'>
                    <path d='M12 2v20M2 12h20M5 5l14 14M19 5L5 19' />
                  </svg>
                ) : (
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#b85f12'
                    strokeWidth='2.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'>
                    <circle cx='12' cy='12' r='9' />
                    <path d='M12 8v5M12 16.5v.5' />
                  </svg>
                )}
                <span>{p.notice.text}</span>
              </div>
            )}

            <div className={styles.spread}>
              <span className={styles.overline}>Today</span>
              {p.todayDone ? (
                <span className={styles.counted}>
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#b85f12'
                    strokeWidth='3.2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                    style={{ position: 'relative', top: 1 }}>
                    <path d='M5 12l5 5L20 7' />
                  </svg>
                  Counted
                </span>
              ) : (
                <span className={`${styles.ends} ${p.endsAlert ? styles.endsAlert : ''}`}>
                  {p.endsText}
                </span>
              )}
            </div>

            <div className={styles.meterRow}>
              <span className={styles.meterLabel}>Flops seen</span>
              <span className={styles.segs} aria-hidden='true'>
                {Array.from({ length: view.flopsNeeded }, (_, i) => (
                  <span key={i} className={`${styles.seg} ${i < view.flops ? styles.segOn : ''}`} />
                ))}
              </span>
              <span
                className={styles.meterValue}
                aria-label={`${view.flops} of ${view.flopsNeeded} flops seen today`}>
                {view.flops} / {view.flopsNeeded}
              </span>
            </div>

            <div className={styles.rule} aria-hidden='true' />

            <span className={styles.overline}>Next status</span>
            <div className={styles.nextRow}>
              <span className={styles.nextLine}>{p.nextLine}</span>
              <span className={styles.nextChip}>{p.nextChip}</span>
            </div>

            <span className={`${styles.overline} ${styles.statusesHead}`}>Statuses</span>
            <div
              ref={railRef}
              className={styles.rail}
              role='region'
              aria-label={`Statuses, ${p.ladder.length} in a row`}
              tabIndex={0}>
              <div className={styles.railInner} role='list'>
                <span
                  className={styles.track}
                  aria-hidden='true'
                  style={{ left: RAIL_TRACK_LEFT, width: (p.ladder.length - 1) * RAIL_STEP }}
                />
                <span
                  className={styles.fill}
                  aria-hidden='true'
                  style={{ left: RAIL_TRACK_LEFT, width: Math.round(p.fillSteps * RAIL_STEP) }}
                />
                {p.ladder.map((r) => (
                  <div
                    key={r.key}
                    role='listitem'
                    className={styles.rung}
                    style={{ width: RAIL_CELL }}
                    aria-label={`${r.name}, ${r.daysLabel}, ${r.avatarLine}, ${r.freezeLine}${
                      r.reached ? ', reached' : r.isNext ? ', next' : ''
                    }`}>
                    <Flame
                      stage={r.stage}
                      size={34}
                      ring={r.isNext}
                      socket={!r.reached && !r.isNext}
                    />
                    <span
                      className={styles.rungName}
                      style={{ color: r.reached || r.isNext ? '#1a0e06' : '#6b4f35' }}>
                      {r.name}
                    </span>
                    <span className={styles.rungDays}>{r.daysLabel}</span>
                    <span className={styles.rungLine}>{r.avatarLine}</span>
                    <span className={styles.rungFreeze}>{r.freezeLine}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.freezeRow}>
              <Freeze size={30} dim={view.freezes === 0} />
              <span className={styles.freezeText}>
                <span className={styles.freezeTitle}>Freezes · {p.freezesHeld}</span>
                <span className={styles.freezeSub}>{p.freezeSub}</span>
              </span>
              {p.boughtAtCap ? (
                <span className={styles.buyQuiet}>{p.buyLabel}</span>
              ) : (
                <button type='button' className={styles.buy} onClick={onBuy}>
                  {p.buyLabel}
                </button>
              )}
            </div>

            <div className={styles.footer}>
              <button type='button' className={styles.howLink} onClick={onHowItWorks}>
                How streaks work
              </button>
              {p.bestLabel && <span className={styles.best}>{p.bestLabel}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(StreakPanel);
